import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Download, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
interface DocItem {
  name: string;
  type: string;
  size: string;
  url: string;
}
interface DocCategory {
  title: string;
  items: DocItem[];
}
interface SubLine {
  name: string;
  categories: DocCategory[];
}
interface ProductLine {
  id: string;
  name: string;
  logo: string | null;
  categories?: DocCategory[];
  subLines?: SubLine[];
}
export function DownloadsPage() {
  const {
    t
  } = useLanguage();
  useEffect(() => {
    document.title = 'Documentación Técnica | SmartImport Internacional';
  }, []);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    SmartFloor: false,
    SmartPanel: false,
    SmartDoor: false,
    SmartStrips: false,
    SmartSofa: false,
    SmartMachinery: false,
    SmartGarden: false,
    General: false
  });
  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const productLines: ProductLine[] = [{
    id: 'SmartFloor',
    name: 'SmartFloor',
    logo: 'https://cdn.magicpatterns.com/uploads/doKdWE88fwcLzdXFibFDGA/Captura_de_pantalla_2025-02-05_101948-removebg-preview.png',
    categories: [{
      title: t('prod.docs.techSheets'),
      items: [{
        name: t('dl.sec1.i1'),
        type: 'PDF',
        size: '1.2 MB',
        url: 'https://drive.google.com/uc?export=download&id=1KVTl6tHOlv1BFGEjPlqcMXhPpaxvrgLB'
      }, {
        name: t('dl.sec1.i4'),
        type: 'PDF',
        size: '1.8 MB',
        url: 'https://drive.google.com/uc?export=download&id=1P4KgIx6CR4fs4qCXed9ucR73zExC7dvt'
      }, {
        name: t('dl.sec1.i5'),
        type: 'PDF',
        size: '1.5 MB',
        url: 'https://drive.google.com/uc?export=download&id=1RCsWIgwO08R1nnM2GLA0y_7McZ8wQHp2'
      }]
    }]
  }, {
    id: 'SmartPanel',
    name: 'SmartPanel',
    logo: 'https://cdn.magicpatterns.com/uploads/pmx8tqFJe4duTghZHoM65P/WhatsApp_Image_2025-01-24_at_12.09.08__3_-removebg-preview-e1739973722182.png',
    subLines: [{
      name: 'SmartPanel PVC',
      categories: [{
        title: t('prod.docs.techSheets'),
        items: [{
          name: t('dl.panel.pvc.tech1'),
          type: 'PDF',
          size: '2.1 MB',
          url: 'https://drive.google.com/file/d/1Fs7vu5F1J8R1EjXW-N16SbaoiKXWR7HT/view?usp=sharing'
        }]
      }]
    }, {
      name: 'SmartPanel Bambú',
      categories: [{
        title: t('prod.docs.techSheets'),
        items: [{
          name: t('dl.panel.bambu.tech1'),
          type: 'PDF',
          size: '1.8 MB',
          url: 'https://drive.google.com/file/d/1hzPl1Wgem5X9gxbvSLnUYCAZqKiH5qBU/view?usp=sharing'
        }, {
          name: t('dl.panel.bambu.tech2'),
          type: 'PDF',
          size: '2.1 MB',
          url: 'https://drive.google.com/file/d/1Fs7vu5F1J8R1EjXW-N16SbaoiKXWR7HT/view?usp=sharing'
        }]
      }]
    }]
  }, {
    id: 'SmartDoor',
    name: 'SmartDoor',
    logo: 'https://cdn.magicpatterns.com/uploads/gg9vSbfbXeYE8Zpb8HjLum/WhatsApp_Image_2025-01-24_at_12.09.08-removebg-preview-e1739973429684.png',
    subLines: [{
      name: 'Puertas WPC',
      categories: [{
        title: t('prod.docs.techSheets'),
        items: [{
          name: t('dl.door.wpc.cert1'),
          type: 'PDF',
          size: '1.5 MB',
          url: 'https://drive.google.com/file/d/1i_yf0-LupXjGFo_bs-V6KO-n4YY84ffK/view?usp=sharing'
        }, {
          name: t('dl.door.wpc.tech1'),
          type: 'PDF',
          size: '2.2 MB',
          url: 'https://drive.google.com/file/d/1I0cIDKbb841zcpWWdAVyT-rb9fJGTOY1/view?usp=sharing'
        }, {
          name: t('dl.door.wpc.tech2'),
          type: 'PDF',
          size: '1.9 MB',
          url: 'https://drive.google.com/file/d/1vdvb8WRS6ywY7bgTg3OIxawz9TelwCRG/view?usp=sharing'
        }]
      }]
    }, {
      name: 'Puertas Blindadas',
      categories: [{
        title: t('prod.docs.techSheets'),
        items: [{
          name: t('dl.door.blindada.tech1'),
          type: 'PDF',
          size: '1.7 MB',
          url: 'https://drive.google.com/file/d/1iekhDIGmVVDsbNcG3q9oOpFhRQtcnTKj/view?usp=sharing'
        }]
      }]
    }]
  }, {
    id: 'SmartStrips',
    name: 'SmartPanel Exterior & Decorativo',
    logo: 'https://cdn.magicpatterns.com/uploads/jpAAR8bA6Y4ztMXGUKUb52/SmartStrips.png',
    categories: [{
      title: t('prod.docs.techSheets'),
      items: [{
        name: t('dl.strips.tech1'),
        type: 'PDF',
        size: '1.3 MB',
        url: 'https://drive.google.com/file/d/1-fKCMLYFNLuVB8J6pvOrDyXauGoWYuVK/view?usp=sharing'
      }, {
        name: t('dl.strips.tech2'),
        type: 'PDF',
        size: '1.6 MB',
        url: 'https://drive.google.com/file/d/1zfc3Zv6pLDEPvtLE7n90okV_GjFkHWLA/view?usp=sharing'
      }]
    }]
  }];
  // Reusable document row renderer
  const renderDocRow = (item: DocItem, itemIdx: number) => <div key={itemIdx} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-md hover:bg-gray-50 transition-colors duration-300 group" data-id="element-242">
      <div className="flex items-center mb-4 sm:mb-0" data-id="element-243">
        <FileText className="h-5 w-5 text-gray-400 mr-4 flex-shrink-0" data-id="element-244" />
        <span className="text-base font-medium text-gray-700 group-hover:text-corporate-dark transition-colors" data-id="element-245">
          {item.name}
        </span>
      </div>
      <div className="flex items-center justify-between sm:justify-end sm:space-x-6 w-full sm:w-auto" data-id="element-246">
        <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full font-semibold text-xs tracking-wider uppercase" data-id="element-247">
          {item.type}
        </span>
        {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center justify-center px-4 py-2 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-300 rounded-md font-medium text-sm ml-4" data-id="element-248">
            <Download className="mr-2 h-4 w-4" data-id="element-249" />
            {t('dl.btn')}
          </a> : <span className="inline-flex items-center justify-center px-4 py-2 border border-gray-200 text-gray-400 bg-gray-50 rounded-md font-medium text-sm ml-4 cursor-default" data-id="element-250">
            <Download className="mr-2 h-4 w-4" data-id="element-251" />
            {t('dl.btn')}
          </span>}
      </div>
    </div>;
  // Render categories (flat list)
  const renderCategories = (categories: DocCategory[]) => <div className="space-y-10" data-id="element-252">
      {categories.map((category, catIdx) => <div key={catIdx} data-id="element-253">
          <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold mb-4 border-b border-gray-100 pb-2" data-id="element-254">
            {category.title}
          </h3>
          <div className="space-y-2" data-id="element-255">
            {category.items.map((item, itemIdx) => renderDocRow(item, itemIdx))}
          </div>
        </div>)}
    </div>;
  // Render subLines (SmartPanel specific)
  const renderSubLines = (subLines: SubLine[]) => <div className="space-y-8" data-id="element-256">
      {subLines.map((sub, subIdx) => <div key={subIdx} className="border border-gray-100 rounded-md overflow-hidden" data-id="element-257">
          <div className="bg-gray-50/70 px-6 py-4 border-b border-gray-100" data-id="element-258">
            <h3 className="text-lg font-heading font-semibold text-corporate-dark" data-id="element-259">
              {sub.name}
            </h3>
          </div>
          <div className="p-6 space-y-8" data-id="element-260">
            {sub.categories.map((category, catIdx) => <div key={catIdx} data-id="element-261">
                <h4 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3 border-b border-gray-50 pb-2" data-id="element-262">
                  {category.title}
                </h4>
                <div className="space-y-1" data-id="element-263">
                  {category.items.map((item, itemIdx) => renderDocRow(item, itemIdx))}
                </div>
              </div>)}
          </div>
        </div>)}
    </div>;
  return <div className="w-full bg-neutral-grey min-h-screen pb-32" data-id="element-264">
      {/* Hero Section */}
      <div className="bg-corporate-dark text-white py-20 md:py-32 px-4" data-id="element-265">
        <div className="max-w-7xl mx-auto text-center mt-10" data-id="element-266">
          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="text-5xl md:text-6xl font-heading font-bold mb-6 tracking-tight" data-id="element-267">
            {t('dl.hero.title')}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="text-xl text-gray-300 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-268">
            {t('dl.hero.desc')}
          </motion.p>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10" data-id="element-269">
        <div className="space-y-8" data-id="element-270">
          {productLines.map((line, idx) => {
          const isOpen = openSections[line.id];
          return <motion.div key={line.id} initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + idx * 0.1,
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="bg-white rounded-sm shadow-md hover:shadow-lg transition-shadow duration-500 ease-premium overflow-hidden border border-gray-100" data-id="element-271">
                {/* Accordion Header */}
                <button onClick={() => toggleSection(line.id)} className="w-full bg-gray-50 px-8 py-6 border-b border-gray-100 flex items-center justify-between hover:bg-gray-100 transition-colors duration-300" data-id="element-272">
                  <div className="flex items-center" data-id="element-273">
                    {line.logo ? <img src={line.logo} alt={line.name} className="h-8 md:h-10 w-auto object-contain" data-id="element-274" /> : <h2 className="text-2xl font-heading font-bold text-corporate-dark" data-id="element-275">
                        {line.name}
                      </h2>}
                  </div>
                  <ChevronDown className={`h-6 w-6 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} data-id="element-276" />
                </button>

                {/* Accordion Body */}
                <AnimatePresence initial={false} data-id="element-277">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden" data-id="element-278">
                      <div className="p-8" data-id="element-279">
                        {line.subLines ? renderSubLines(line.subLines) : line.categories ? renderCategories(line.categories) : null}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </div>;
}